/**
 * QOE-TRUTH-AND-JOURNEY-1 §3 (R9-R12) — the Journey Rail.
 *
 * A read-only, two-layer status strip, not a wizard:
 *   1. The spine — which of the six FORWARD_STATUS_ORDER stages the deal is
 *      in. Coarse, monotonic, from the status (R9). Rendered with the
 *      done-tick doctrine of `frontend/src/components/attest/WizardStepNav.tsx`
 *      — green means "this part is finished", nothing more.
 *   2. The gates inside the current stage — the finer, checkable conditions
 *      blocking the next hop (R10). Every gate here is exactly what the API
 *      sent; this component invents no gate, no label, no met/unmet state of
 *      its own (R11) — that would be the disabled-button problem with more
 *      pixels (brief §3c).
 *
 * Unlike WizardStepNav this rail does not navigate — QoE's stages are not
 * screens a CPA jumps between (document collection, normalization and
 * findings legitimately overlap; brief §3c). There is no `onSelect`. It only
 * answers "where is this deal, and what's next" — the action itself lives on
 * the button/tab that already does the work.
 *
 * Generic over `(stages, current, completed, gates, nextAction)` (R12) so a
 * later product can adopt it under its own ID (R13 explicitly does not
 * migrate Attest/Valuation/OTCID in this batch) without this file changing.
 */
import { ArrowRight, Check, Circle } from 'lucide-react'

export interface JourneyGate {
  key: string
  label: string
  met: boolean
  /** Countable-state gates carry a detail string, e.g. "3 of 8". Optional —
   *  a boolean-only gate (e.g. "Scope set") has nothing to add here. */
  detail?: string
}

export interface JourneyRailProps {
  /** The product's forward spine, sourced from its API — never hardcoded
   *  by a caller of this component (R9). */
  stages: string[]
  /** The deal/entity's current status. May be off-spine (e.g. QoE's
   *  "withdrawn") — see isOffSpine below; R9 renders that as a terminal
   *  state, not a stage. */
  current: string
  /** Stages strictly behind `current` on the spine. */
  completed: string[]
  /** The gate(s) blocking the hop out of `current` — already scoped to the
   *  current stage by the caller's API, not a cumulative list of every gate
   *  ever satisfied. */
  gates: JourneyGate[]
  /** The one next action, named as a verb (R10) — null once there is
   *  nothing left to do on this spine (e.g. "closed"). */
  nextAction: { label: string } | null
  /** Optional key -> display label map. Falls back to Title Case of the
   *  key so a caller with no labels of its own still gets something
   *  readable. */
  stageLabels?: Record<string, string>
}

function defaultLabel(key: string): string {
  return key
    .split('_')
    .map((w) => (w.length ? w[0].toUpperCase() + w.slice(1) : w))
    .join(' ')
}

export function JourneyRail({
  stages, current, completed, gates, nextAction, stageLabels,
}: JourneyRailProps) {
  const label = (key: string) => stageLabels?.[key] ?? defaultLabel(key)
  const isOffSpine = !stages.includes(current)

  if (isOffSpine) {
    return (
      <div
        className="rounded-lg border bg-muted/30 px-4 py-2.5 text-sm text-muted-foreground"
        data-testid="journey-rail-terminal"
      >
        {label(current)}
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-2.5" data-testid="journey-rail">
      {/* Layer 1: the spine */}
      <nav className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm" aria-label="Deal stage">
        {stages.map((s, i) => {
          const isDone = completed.includes(s)
          const isCurrent = s === current
          return (
            <span key={s} className="flex items-center gap-1.5">
              {i > 0 && (
                <span className="text-muted-foreground/50" aria-hidden="true">·</span>
              )}
              <span
                data-testid={`journey-stage-${s}`}
                // Read, not decorative — same doctrine as WizardStepNav's
                // data-complete: state belongs on the element assistive
                // tech and tests can both key off, not just in the icon.
                data-complete={isDone ? 'true' : undefined}
                data-current={isCurrent ? 'true' : undefined}
                className={
                  isDone
                    ? 'inline-flex items-center gap-1 font-medium text-emerald-700'
                    : isCurrent
                      ? 'inline-flex items-center gap-1 font-semibold text-foreground'
                      : 'text-muted-foreground'
                }
              >
                {isDone && (
                  <Check aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
                )}
                {isCurrent && !isDone && (
                  <Circle aria-hidden="true" className="h-2 w-2 shrink-0 fill-current" />
                )}
                {label(s)}
              </span>
            </span>
          )
        })}
      </nav>

      {/* Layer 2: the gates blocking the hop out of `current` (R10, R11) */}
      {gates.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
          {gates.map((g) => (
            <span
              key={g.key}
              data-testid={`journey-gate-${g.key}`}
              data-met={g.met ? 'true' : 'false'}
              className={
                g.met
                  ? 'inline-flex items-center gap-1 text-emerald-700'
                  : 'inline-flex items-center gap-1 text-amber-700'
              }
            >
              {g.met ? (
                <Check aria-hidden="true" className="h-3 w-3 shrink-0" />
              ) : (
                <Circle aria-hidden="true" className="h-2 w-2 shrink-0" />
              )}
              {g.label}
              {g.detail ? ` — ${g.detail}` : ''}
            </span>
          ))}
        </div>
      )}

      {/* One next action, a verb (R10) */}
      {nextAction && (
        <p
          className="mt-2 flex items-center gap-1 text-xs font-medium text-foreground"
          data-testid="journey-next-action"
        >
          <ArrowRight aria-hidden="true" className="h-3 w-3 shrink-0" />
          Next: {nextAction.label}
        </p>
      )}
    </div>
  )
}
